import { zodResolver } from "@hookform/resolvers/zod";
import { forwardRef, useEffect } from "react";
import { useForm } from "react-hook-form";

import { CustomCardBody, Switch } from "@/components/atoms";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { useWizardStepDraft } from "@/modules/wizardDraft";
import type { FormProps, FormRefType } from "@/types/common";
import { useExposeFormValues } from "@/utils/hooks/useExposeFormValues";

import CustomTimePicker from "./CustomTimePicker";
import { BranchHoursFormSchema, type BranchHoursFormType } from "./types";

type BranchHoursFormProps = FormProps<BranchHoursFormType> & {
  draftEntityId?: string;
};

const BranchHoursForm = forwardRef<
  FormRefType<BranchHoursFormType>,
  BranchHoursFormProps
>((props, ref) => {
  const {
    defaultValues,
    onSubmit,
    onValidityChange,
    id,
    classes,
    draftEntityId,
  } = props;
  const form = useForm<BranchHoursFormType>({
    resolver: zodResolver(BranchHoursFormSchema),
    defaultValues,
    mode: "onChange",
    reValidateMode: "onBlur",
  });

  useWizardStepDraft({
    form,
    wizard: "branch",
    entityId: draftEntityId,
    step: "businessHours",
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isValid },
  } = form;

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  const applyToAll = (index: number) => {
    const currentHours = watch("schedules");
    const { open, close } = currentHours[index];

    currentHours.forEach((hour, i) => {
      if (!hour.closed && i !== index) {
        setValue(`schedules.${i}.open`, open);
        setValue(`schedules.${i}.close`, close);
      }
    });
  };

  useEffect(() => {
    const subscription = watch((_value, { name }) => {
      if (name?.includes("open") || name?.includes("close")) {
        form.trigger();
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, form]);

  useExposeFormValues(ref, form);

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        id={id}
        data-testid="branch-hours-form"
      >
        <CustomCardBody classes={cn("max-w-[877px]", classes)}>
          <div className="flex flex-col gap-5">
            <p className="text-sm">Set standard business working hours:</p>
            {watch("schedules").map((hour, index) => (
              <FormItem
                key={index}
                className={cn(
                  "flex items-center gap-5 xl:flex-nowrap flex-wrap"
                )}
              >
                <p className="text-sm font-bold shrink-0 w-[100px]">
                  {`${hour.day[0].toUpperCase()}${hour.day.slice(1)}`}
                </p>

                <div className="flex items-center w-full justify-between gap-5 flex-wrap sm:flex-nowrap">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FormField
                        control={control}
                        name={`schedules.${index}.closed`}
                        render={({ field }) => (
                          <>
                            <FormControl>
                              <Switch
                                checked={!field.value}
                                onCheckedChange={val => field.onChange(!val)}
                                data-testid={`branch-hours-switch-${hour.day}`}
                              />
                            </FormControl>
                            <p className="text-sm">
                              {!field.value ? "Open" : "Closed"}
                            </p>
                          </>
                        )}
                      />
                    </div>
                  </div>
                  <CustomTimePicker
                    hour={hour}
                    index={index}
                    applyToAll={applyToAll}
                  />
                </div>
              </FormItem>
            ))}
          </div>
        </CustomCardBody>
      </form>
    </Form>
  );
});

BranchHoursForm.displayName = "BranchHoursForm";
export default BranchHoursForm;
